import React from "react";
import { Link } from "react-router-dom";
import { BookImage } from "./BookImage";
import { useBooks } from "../hooks";
import { analyticsService } from "../services/analytics/analyticsService";

interface Category {
  id: string;
  name: string;
  slug: string;
  subtitle?: string | null;
  description?: string | null;
  color?: string | null;
  iconUrl?: string | null;
  displayOrder: number;
  bookCount?: number;
}

interface CategorySectionProps {
  category: Category;
  className?: string;
}

const CategorySection: React.FC<CategorySectionProps> = ({ category, className = "" }) => {
  // Fetch books for this category
  const {
    data: booksData,
    isLoading,
    error,
  } = useBooks({
    page: 1,
    limit: 6,
    categoryIds: [category.id],
  });

  const books = booksData?.books || [];

  return (
    <section className={`pb-5 ${className}`} aria-labelledby={`category-${category.id}`}>
      {/* Category Header - Card style matching Figma */}
      <div className="flex items-start justify-between gap-4 pt-5 pb-0 mb-5">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h2
            id={`category-${category.id}`}
            className="text-lg font-semibold text-[var(--color-text-primary)] leading-7"
          >
            {category.name}
          </h2>
          {/* Subtitle/Description */}
          {category.subtitle ? (
            <p className="text-sm text-[var(--color-text-secondary)] truncate mt-0.5">
              {category.subtitle}
            </p>
          ) : category.description ? (
            <p className="text-sm text-[var(--color-text-secondary)] truncate mt-0.5">
              {category.description}
            </p>
          ) : null}
        </div>
        {/* View more button */}
        <Link
          to={`/gallery?category=${category.id}`}
          className="shrink-0 px-3.5 py-2.5 text-sm font-semibold text-[#857E76] bg-[var(--color-bg-surface)] border border-[var(--color-border-default)] rounded-lg hover:bg-[var(--color-bg-page)] transition-colors"
        >
          View more
        </Link>
      </div>

      {/* Horizontal Scrolling Book Grid - Figma style */}
      <div
        className="overflow-x-auto scrollbar-hide"
        role="region"
        aria-labelledby={`category-${category.id}`}
      >
        <div className="flex gap-4">
          {error ? (
            <div className="text-center text-[var(--color-error-500)] py-8 w-full">
              Failed to load books for this category
            </div>
          ) : isLoading ? (
            // Loading skeleton - 8 items matching Figma
            Array.from({ length: 8 }, (_, index) => (
              <div key={index} className="flex-none w-[140px] aspect-[400/640]">
                <div className="w-full h-full bg-[var(--color-border-subtle)] rounded-lg animate-pulse" />
              </div>
            ))
          ) : books.length === 0 ? (
            <div className="text-center text-[var(--color-text-secondary)] py-8 w-full">
              No books found in this category
            </div>
          ) : (
            // Book covers - matching Figma grid with fixed width
            books.map(book => (
              <Link
                key={book.id}
                to={`/book/${book.id}`}
                onClick={() => {
                  analyticsService.track("book.card_clicked", {
                    book_id: book.id,
                    book_title: book.title,
                  });
                }}
                className="flex-none w-[140px] group"
              >
                <BookImage
                  bookId={book.id}
                  title={book.title}
                  className="aspect-[400/640] rounded-lg group-hover:shadow-lg transition-shadow"
                  sizes="140px"
                />
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default CategorySection;
